import useFocus from "@/lib/hooks/interactions/other/useFocus";
import { ButtonIconPropsType, Icon, useHover, useLocales } from "@/lib/main";
import { CSSProperties } from "react";

export default function ButtonIcon({
  ariaLabel,
  buttonRef,
  disabled,
  iconColor,
  iconName,
  onClick,
  size,
  style,
  title,
}: ButtonIconPropsType) {
  const l = useLocales();
  const { isFocused, setIsFocused, onFocus, onBlur } = useFocus();
  const { isHovered, setIsHovered, onMouseEnter, onMouseLeave } = useHover();

  const hasTouchLikePrimaryInput =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none), (pointer: coarse)").matches;

  const effectiveIsHovered = !hasTouchLikePrimaryInput && isHovered;
  const effectiveIsFocused = !hasTouchLikePrimaryInput && isFocused;

  const handleClick = () => {
    setIsFocused(false);
    setIsHovered(false);
    if (onClick) onClick();

    if (buttonRef && typeof buttonRef !== "function" && buttonRef.current) {
      buttonRef.current.blur();
    }
  };

  const buttonStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    border: "none",
    borderRadius: "50%",
    padding: "0.5rem",

    backgroundColor: effectiveIsHovered
      ? "var(--color-icon-hover)"
      : effectiveIsFocused
        ? "var(--color-icon-focus)"
        : "transparent",

    ...style, // Apply the passed-in style prop
  };

  return (
    <button
      aria-label={ariaLabel ? l(ariaLabel) : iconName}
      disabled={disabled}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      ref={buttonRef}
      style={buttonStyle}
      title={title ? l(title) : ""}
    >
      <Icon
        color={iconColor || "secondary"}
        name={iconName || "placeholder"}
        size={size}
      />
    </button>
  );
}
